import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Target,
  Zap,
  Shield,
  Users,
  BarChart3,
  Globe,
  Smartphone,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import './nextsection.css';
import a from './google.png';
import b from './meta.png'

const NextSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Target,
      title: 'Precision Targeting',
      description:
        'AI-powered audience segmentation that finds your perfect customers across all platforms.',
    },
    {
      icon: TrendingUp,
      title: 'Growth Analytics',
      description:
        'Real-time insights and performance tracking that drives continuous optimization.',
    },
    {
      icon: Globe,
      title: 'Multi-Platform Reach',
      description:
        'Seamless campaigns across Google, Meta, TikTok, Amazon, and emerging platforms.',
    },
    {
      icon: Shield,
      title: 'Brand Protection',
      description:
        'Advanced monitoring and reputation management to safeguard your brand identity.',
    },
  ];

  const platforms = [
    { name: 'Meta Ads ', growth: '+450%' },
    { name: 'Google Ads  ', growth: '+340%' },
    { name: 'TikTok Ads ', growth: '+230%' },
    { name: 'Amazon PPC ', growth: '+250%' },
    { name: 'YouTube Ads', growth: '+490%' },
    { name: 'Bing Ads   ', growth: '+180%' },
  ];

  const benefits = [
    '300% average ROI increase within 90 days',
    '50% reduction in customer acquisition cost',
    'Real-time performance tracking and optimization',
    'Dedicated account management and support',
    'Custom attribution modeling and reporting',
    'Advanced audience insights and segmentation',
  ];

  return (
    <section ref={sectionRef} className="next-section">
      <div className="section-bg">
        <div className="gradient-orb orb-4"></div>
        <div className="gradient-orb orb-5"></div>
        <div className="floating-particles"></div>
      </div>

      <div className="container">
        {/* Main Content Block */}
        <div className="content-block animate-on-scroll">
          <div className="section-badge">
            <Zap className="badge-icon" />
            <span>Your Growth Engine for Digital Excellence</span>
          </div>

          <h2 className="section-title">
            We're more than just an agency;
            <span className="highlight"> we're your growth engine</span>
          </h2>

          <p className="section-description">
            Transform your digital presence with our comprehensive suite of marketing solutions.
            From precision targeting to advanced analytics, we deliver results that matter.
          </p>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card animate-on-scroll">
                <div className="feature-icon">
                  <feature.icon />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Performance */}
        <div className="content-block animate-on-scroll">
          <h2 className="section-title">
            Drive Growth with Great Ads &
            <span className="highlight"> Advanced Multi-Funnel Marketing</span>
          </h2>

          <p className="section-description">
            Make your brand stand out with ads that connect everywhere your audience is online. With smart insights and the right strategy, we help you get the most impact across every digital channel.
          </p>

     <div className="platforms-showcase">
  <div className="platforms-grid">
    {platforms.map((platform, index) => {
      // Remove + and % and convert to number
      const growthValue = parseInt(platform.growth.replace(/\D/g, ''));

      // Find the maximum growth in the array
      const maxGrowth = Math.max(...platforms.map(p => parseInt(p.growth.replace(/\D/g, ''))));

      // Scale height relative to maxGrowth
      const heightPercent = (growthValue / maxGrowth) * 100;

      return (
        <div key={index} className="platform-card animate-on-scroll">
          <div className="platform-info">
            <h4 className="platform-name">{platform.name}</h4>
            <div className="platform-growth">{platform.growth}</div>
          </div>
          <div className="platform-chart">
     <div
  className="chart-bar"
  style={{
    height: '90%',
    width: '20px',
    margin: '0 auto',
  }}
></div>

          </div>
        </div>
      );
    })}
  </div>
</div>


          <div className="strategy-points">
            <div className="strategy-point animate-on-scroll">
              <div className="point-icon">
                <Target />
              </div>
              <div className="point-content">
                <h4>Win on Search</h4>
                <p>
                  Own Google and Bing with focused ads that find customers ready to buy, right when
                  they're searching.
                </p>
              </div>
            </div>

            <div className="strategy-point animate-on-scroll">
              <div className="point-icon">
                <Users />
              </div>
              <div className="point-content">
                <h4>Engage on Social</h4>
                <p>
                  Connect and convert with data-driven, eye-catching ads for Meta, TikTok, and other
                  platforms.
                </p>
              </div>
            </div>

            <div className="strategy-point animate-on-scroll">
              <div className="point-icon">
                <Smartphone />
              </div>
              <div className="point-content">
                <h4>Sell More on Marketplaces</h4>
                <p>
                  Maximize visibility and sales on Amazon, eBay, and Walmart with optimized
                  marketplace strategies.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Framework Section */}
        <div className="content-block framework-section animate-on-scroll">
          <div className="framework-header">
            <h2 className="section-title">
              The Beyond Framework:
              <span className="highlight"> Beyond Numbers. Beyond Limits.</span>
            </h2>

            <p className="section-description">
              Our proven methodology combines cutting-edge technology with human expertise to
              deliver sustainable growth that goes beyond traditional metrics.
            </p>
          </div>

          <div className="framework-content">
            <div className="framework-pillar animate-on-scroll">
              <div className="pillar-header">
                <BarChart3 className="pillar-icon" />
                <h3>Real Results, Not Just Clicks</h3>
              </div>
              <p>
                We focus on what truly helps your business grow. From increased sales and quality
                leads to stronger customer loyalty, everything we do drives long-term success with
                full transparency and measurable outcomes.
              </p>
            </div>

            <div className="framework-pillar animate-on-scroll">
              <div className="pillar-header">
                <Globe className="pillar-icon" />
                <h3>Seamless Multi-Channel Integration</h3>
              </div>
              <p>
                Your customers don't stick to one platform, and neither do we. Our unified campaigns
                work across all channels, guiding your audience from discovery to conversion with
                real-time optimization and consistent messaging.
              </p>
            </div>

            <div className="framework-pillar animate-on-scroll">
              <div className="pillar-header">
                <Shield className="pillar-icon" />
                <h3>Transparency at Our Core</h3>
              </div>
              <p>
                Experience total transparency with real-time reporting, clear tracking, and open
                communication. Every decision is data-driven and focused solely on your business
                success, building trust through honest, measurable results.
              </p>
            </div>
          </div>

          <div className="benefits-list animate-on-scroll">
            <h3 className="benefits-title">What You Get:</h3>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="cta-section animate-on-scroll">
       <Link to="/consultation">
  <button className="cta-button">
    <span>Scale Your Business</span>
    <ArrowRight className="cta-icon" />
  </button>
</Link>
            <p className="cta-note">
              Free consultation • No long-term contracts • Results guaranteed
            </p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default NextSection;